import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { Database } from '@fluent/types';
import { authLock } from './authLock.js';

/**
 * The single Supabase client used by the app. All components read/write via
 * this instance so auth state + realtime subscriptions share one socket.
 *
 * URL + anon key are compile-time-baked via Vite's `import.meta.env.VITE_*`.
 */

const url = import.meta.env.VITE_SUPABASE_URL as string | undefined;
const anon = import.meta.env.VITE_SUPABASE_ANON_KEY as string | undefined;

if (!url || !anon) {
  throw new Error(
    'Missing VITE_SUPABASE_URL or VITE_SUPABASE_ANON_KEY env — populate apps/app/.env.local from the workspace .env.local',
  );
}

export const supabase: SupabaseClient<Database> = createClient<Database>(url, anon, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Implicit flow over PKCE. PKCE requires the code_verifier that was
    // stored in localStorage before the Google redirect to still be there
    // when the user comes back; on iOS Safari (and other privacy-strict
    // mobile browsers) the verifier sometimes can't be exchanged after
    // a cross-origin redirect, leaving the user with a code-verifier in
    // storage but no session — confirmed via Tornike's /auth/debug
    // 2026-04-28. Implicit flow returns the session in the URL hash, no
    // second exchange request, no cross-redirect storage dependency.
    flowType: 'implicit',
    // Supabase's lock with one upstream bug fixed — see authLock.ts.
    lock: authLock,
  },
});
